import * as React from 'react';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import Alert from '@mui/material/Alert';
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import CssBaseline from '@mui/material/CssBaseline';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import Divider from '@mui/material/Divider';
import Grid from '@mui/material/Grid';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemText from '@mui/material/ListItemText';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepButton from '@mui/material/StepButton';
import Stepper from '@mui/material/Stepper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import ArrowBackRoundedIcon from '@mui/icons-material/ArrowBackRounded';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import BusinessRoundedIcon from '@mui/icons-material/BusinessRounded';
import CloudUploadRoundedIcon from '@mui/icons-material/CloudUploadRounded';
import HomeRoundedIcon from '@mui/icons-material/HomeRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import SaveRoundedIcon from '@mui/icons-material/SaveRounded';
import axiosInstance from '../utils/axiosInstance';
import { setPartnerProfile } from '../redux/authSlice';

const companyTypeOptions = [
  { value: 'manufacturer', label: 'Manufacturer' },
  { value: 'service_provider', label: 'Service Provider' },
  { value: 'systems_integrator', label: 'Systems Integrator' },
  { value: 'distributor', label: 'Distributor' },
  { value: 'government', label: 'Government' },
  { value: 'academic', label: 'Academic' },
  { value: 'other', label: 'Other' },
];

const cmmcOptions = [
  { value: '', label: 'Not specified' },
  { value: 'level_1', label: 'Level 1' },
  { value: 'level_2', label: 'Level 2' },
  { value: 'level_3', label: 'Level 3' },
  { value: 'not_applicable', label: 'Not applicable' },
];

const onboardingSteps = [
  { key: 'welcome', label: 'Welcome' },
  { key: 'partner_profile', label: 'Partner Profile' },
  { key: 'partner_users', label: 'Invite Users' },
];

const emptyAddress = {
  line1: '',
  line2: '',
  city: '',
  region: '',
  postal_code: '',
  country: 'US',
};

const emptyProfileForm = {
  physical_main_address: '',
  physical_address: { ...emptyAddress },
  profile_description: '',
  logo_file_id: '',
  company_phone: '',
  website: '',
  company_type: '',
  cmmc_compliance_level: '',
  government_entity: '',
};

const emptyInviteForm = {
  email: '',
  full_name: '',
  role: 'partner_user',
};

const inviteRoleOptions = [
  { value: 'partner_user', label: 'User' },
  { value: 'partner_admin', label: 'Admin' },
];

function parentPartnerFromProfile(partnerProfile) {
  if (partnerProfile?.onboarding?.partner) {
    return partnerProfile.onboarding.partner;
  }
  const memberships = Array.isArray(partnerProfile?.memberships) ? partnerProfile.memberships : [];
  return memberships.find((membership) => (
    membership?.role === 'partner_admin' && membership?.partner_type === 'parent'
  )) || null;
}

function cloneAddress(address = {}) {
  return {
    ...emptyAddress,
    ...address,
    region: address.region || address.state || '',
    postal_code: address.postal_code || address.zipcode || address.zip || '',
    country: address.country || 'US',
  };
}

function normalizeAddress(value, fallbackText = '') {
  const source = value && typeof value === 'object' ? value : {};
  const address = cloneAddress(source);
  if (!address.line1 && fallbackText) {
    address.line1 = fallbackText;
  }
  return address;
}

function formatAddress(address) {
  const parts = [
    address?.line1,
    address?.line2,
    address?.city,
    address?.region,
    address?.postal_code,
    address?.country,
  ].map((value) => String(value || '').trim()).filter(Boolean);
  return parts.join(', ');
}

function addressPayload(address) {
  const payload = cloneAddress(address);
  const formatted = formatAddress(payload);
  if (formatted) {
    payload.formatted = formatted;
  }
  return payload;
}

function profileFormFromPartner(partner) {
  return {
    physical_main_address: partner?.physical_main_address || partner?.physical_address?.formatted || '',
    physical_address: normalizeAddress(partner?.physical_address, partner?.physical_main_address || ''),
    profile_description: partner?.profile_description || '',
    logo_file_id: partner?.logo_file_id || '',
    company_phone: partner?.company_phone || partner?.contact_phone || '',
    website: partner?.website || '',
    company_type: partner?.company_type || '',
    cmmc_compliance_level: partner?.cmmc_compliance_level || '',
    government_entity: partner?.government_entity === true
      ? 'true'
      : partner?.government_entity === false
        ? 'false'
        : '',
  };
}

function normalizedEmail(value) {
  return String(value || '').trim().toLowerCase();
}

function errorMessage(err, fallback) {
  const message = err?.response?.data?.message || err?.message || fallback;
  const requestId = err?.response?.data?.request_id;
  return requestId ? `${message} (request ${requestId})` : message;
}

function requiredProfileFieldsComplete(partnerProfile, partner) {
  const missing = Array.isArray(partnerProfile?.onboarding?.missing_required_fields)
    ? partnerProfile.onboarding.missing_required_fields.filter((field) => field !== 'logo_file_id')
    : [];
  return partner?.profile_status === 'complete' && missing.length === 0;
}

function addressComplete(address) {
  return Boolean(
    String(address?.line1 || '').trim()
    && String(address?.city || '').trim()
    && String(address?.region || '').trim()
    && String(address?.postal_code || '').trim()
  );
}

export default function PartnerOnboardingPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const partnerProfile = useSelector((state) => state.auth.partnerProfile);
  const partner = useMemo(() => parentPartnerFromProfile(partnerProfile), [partnerProfile]);
  const onboarding = partnerProfile?.onboarding || {};
  const profileLogoUrlRef = useRef('');
  const [currentStep, setCurrentStep] = useState('welcome');
  const [profileForm, setProfileForm] = useState(emptyProfileForm);
  const [profileLogoPreviewUrl, setProfileLogoPreviewUrl] = useState('');
  const [sentInvitations, setSentInvitations] = useState([]);
  const [inviteForm, setInviteForm] = useState(emptyInviteForm);
  const [uploadingLogoKey, setUploadingLogoKey] = useState('');
  const [savingKey, setSavingKey] = useState('');
  const [skipConfirmOpen, setSkipConfirmOpen] = useState(false);
  const [error, setError] = useState('');

  const profileIsComplete = requiredProfileFieldsComplete(partnerProfile, partner);
  const partnerUsersComplete = Boolean(onboarding.partner_users_completed || onboarding.status === 'complete');
  const partnerName = partner?.display_name || partner?.partner_code || 'Partner';
  const activeStep = Math.max(0, onboardingSteps.findIndex((step) => step.key === currentStep));
  useEffect(() => {
    setProfileForm(profileFormFromPartner(partner));
  }, [partner]);

  useEffect(() => {
    let active = true;
    const fileId = partner?.logo_file_id;

    if (!fileId) {
      setProfileLogoPreviewUrl('');
      return undefined;
    }

    async function loadLogoPreview() {
      try {
        const response = await axiosInstance.post(`/capability-media/${encodeURIComponent(fileId)}/download`);
        if (active) {
          setProfileLogoPreviewUrl(response.data?.url || '');
        }
      } catch {
        if (active) {
          setProfileLogoPreviewUrl('');
        }
      }
    }

    loadLogoPreview();
    return () => {
      active = false;
    };
  }, [partner?.logo_file_id]);

  useEffect(() => () => {
    if (profileLogoUrlRef.current) {
      URL.revokeObjectURL(profileLogoUrlRef.current);
    }
  }, []);

  function canOpenStep(stepKey) {
    if (stepKey === 'welcome' || stepKey === 'partner_profile') {
      return true;
    }
    if (stepKey === 'partner_users') {
      return profileIsComplete || currentStep === 'partner_users';
    }
    return false;
  }

  function stepCompleted(stepKey) {
    if (stepKey === 'welcome') {
      return currentStep !== 'welcome';
    }
    if (stepKey === 'partner_profile') {
      return profileIsComplete;
    }
    if (stepKey === 'partner_users') {
      return partnerUsersComplete;
    }
    return false;
  }

  function openStep(stepKey) {
    if (canOpenStep(stepKey)) {
      setCurrentStep(stepKey);
      setError('');
    }
  }

  function nextFromWelcome() {
    if (!profileIsComplete) {
      openStep('partner_profile');
      return;
    }
    openStep('partner_users');
  }

  function updateProfileField(field, value) {
    setProfileForm((current) => ({ ...current, [field]: value }));
  }

  function updateProfileAddress(field, value) {
    setProfileForm((current) => {
      const physicalAddress = { ...current.physical_address, [field]: value };
      return {
        ...current,
        physical_address: physicalAddress,
        physical_main_address: formatAddress(physicalAddress),
      };
    });
  }

  function updateInviteField(field, value) {
    setInviteForm((current) => ({ ...current, [field]: value }));
  }

  async function refreshPartnerProfile() {
    const profileResponse = await axiosInstance.get('/partners/user');
    dispatch(setPartnerProfile(profileResponse.data));
    return profileResponse.data;
  }

  async function uploadLogo(file) {
    if (!partner?.partner_id || !file) {
      return;
    }
    if (!file.type?.startsWith('image/')) {
      setError('Logo must be an image file.');
      return;
    }

    setUploadingLogoKey('profile');
    setError('');
    try {
      const uploadResponse = await axiosInstance.post('/capability-media/upload', {
        partner_id: partner.partner_id,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        target_kind: 'partner_logo',
      });
      const { url, fields, file_id: fileId } = uploadResponse.data || {};
      if (!url || !fileId) {
        throw new Error('Logo upload did not return a target URL.');
      }

      const formData = new FormData();
      Object.entries(fields || {}).forEach(([key, value]) => {
        formData.append(key, value);
      });
      formData.append('file', file);

      const s3Response = await fetch(url, {
        method: 'POST',
        body: formData,
      });
      if (!s3Response.ok) {
        throw new Error('Logo upload failed.');
      }

      const localUrl = URL.createObjectURL(file);
      if (profileLogoUrlRef.current) {
        URL.revokeObjectURL(profileLogoUrlRef.current);
      }
      profileLogoUrlRef.current = localUrl;
      setProfileLogoPreviewUrl(localUrl);
      updateProfileField('logo_file_id', fileId);
    } catch (err) {
      setError(errorMessage(err, 'Unable to upload logo.'));
    } finally {
      setUploadingLogoKey('');
    }
  }

  async function submitProfile(event) {
    event.preventDefault();
    if (!partner?.partner_id) {
      setError('Partner profile was not found.');
      return;
    }
    if (!addressComplete(profileForm.physical_address)) {
      setError('Address line 1, city, state, and zip code are required.');
      return;
    }
    if (profileForm.government_entity === '') {
      setError('Government entity selection is required.');
      return;
    }

    const physicalAddress = addressPayload(profileForm.physical_address);
    setSavingKey('profile');
    setError('');
    try {
      const updatedProfile = await axiosInstance.patch('/partner-admin/profile', {
        partner_id: partner.partner_id,
        physical_main_address: formatAddress(physicalAddress),
        physical_address: physicalAddress,
        profile_description: profileForm.profile_description,
        logo_file_id: profileForm.logo_file_id || null,
        company_phone: profileForm.company_phone,
        website: profileForm.website,
        company_type: profileForm.company_type,
        cmmc_compliance_level: profileForm.cmmc_compliance_level || null,
        government_entity: profileForm.government_entity === 'true',
      });
      const refreshed = await refreshPartnerProfile();
      if (!updatedProfile.data?.onboarding?.required && !refreshed?.onboarding?.required) {
        navigate('/', { replace: true });
        return;
      }
      setCurrentStep('partner_users');
    } catch (err) {
      setError(errorMessage(err, 'Unable to complete partner profile.'));
    } finally {
      setSavingKey('');
    }
  }

  async function submitInvite(event) {
    event.preventDefault();
    const email = normalizedEmail(inviteForm.email);
    if (!email) {
      setError('Email is required.');
      return;
    }
    if (sentInvitations.some((invitation) => normalizedEmail(invitation.email) === email)) {
      setError('An invitation has already been sent to this email address.');
      return;
    }

    setSavingKey('invite');
    setError('');
    try {
      const response = await axiosInstance.post('/partner-admin/users', {
        email,
        full_name: inviteForm.full_name,
        role: inviteForm.role,
      });
      setSentInvitations((current) => [
        ...current,
        {
          ...response.data,
          email,
          full_name: response.data?.full_name || inviteForm.full_name,
          role: response.data?.role || inviteForm.role,
        },
      ]);
      setInviteForm(emptyInviteForm);
    } catch (err) {
      setError(errorMessage(err, 'Unable to send partner invitation.'));
    } finally {
      setSavingKey('');
    }
  }

  async function completeOnboarding() {
    setSavingKey('complete');
    setError('');
    try {
      await axiosInstance.post('/partner-admin/onboarding/complete');
      const refreshed = await refreshPartnerProfile();
      if (!refreshed?.onboarding?.required) {
        navigate('/', { replace: true });
      }
    } catch (err) {
      setError(errorMessage(err, 'Unable to finish onboarding.'));
    } finally {
      setSavingKey('');
      setSkipConfirmOpen(false);
    }
  }

  function confirmFinishWithoutInvitations() {
    setSkipConfirmOpen(true);
  }

  const profileBusy = savingKey === 'profile' || uploadingLogoKey === 'profile';
  const inviteBusy = savingKey === 'invite' || savingKey === 'complete';
  const pageTitle = currentStep === 'welcome'
    ? 'Welcome to AMX xConsole'
    : currentStep === 'partner_users'
      ? 'Invite Partner Users'
      : 'Complete Partner Profile';

  function renderStepTracker() {
    return (
      <Stepper activeStep={activeStep} alternativeLabel sx={{ mb: 1 }}>
        {onboardingSteps.map((step) => (
          <Step key={step.key} completed={stepCompleted(step.key)}>
            <StepButton
              disabled={!canOpenStep(step.key)}
              onClick={() => openStep(step.key)}
            >
              {step.label}
            </StepButton>
          </Step>
        ))}
      </Stepper>
    );
  }

  function renderWelcomeStep() {
    return (
      <Stack spacing={3}>
        <Box>
          <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
            Welcome, {partnerName}
          </Typography>
          <Typography color="text.secondary" sx={{ maxWidth: 760 }}>
            Your partner account is ready. Confirm the partner profile and optionally invite partner users before
            continuing to xConsole. Business units can be created later from Partner Administration.
          </Typography>
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="flex-end">
          <Button
            type="button"
            variant="contained"
            endIcon={<ArrowForwardRoundedIcon />}
            onClick={nextFromWelcome}
          >
            Next
          </Button>
        </Stack>
      </Stack>
    );
  }

  function renderProfileStep() {
    return (
      <Box component="form" onSubmit={submitProfile}>
        <Stack spacing={3}>
          {error ? <Alert severity="error">{error}</Alert> : null}

          <Grid container spacing={2.5}>
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <TextField label="Partner Code" value={partner?.partner_code || ''} fullWidth disabled />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField label="Legal Name" value={partner?.legal_name || ''} fullWidth disabled />
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                    Physical Main Address
                  </Typography>
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 1"
                    value={profileForm.physical_address.line1}
                    onChange={(event) => updateProfileAddress('line1', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="Address Line 2"
                    value={profileForm.physical_address.line2}
                    onChange={(event) => updateProfileAddress('line2', event.target.value)}
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="City"
                    value={profileForm.physical_address.city}
                    onChange={(event) => updateProfileAddress('city', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={3}>
                  <TextField
                    label="State"
                    value={profileForm.physical_address.region}
                    onChange={(event) => updateProfileAddress('region', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={4}>
                  <TextField
                    label="Zip Code"
                    value={profileForm.physical_address.postal_code}
                    onChange={(event) => updateProfileAddress('postal_code', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    label="High-Level Description"
                    value={profileForm.profile_description}
                    onChange={(event) => updateProfileField('profile_description', event.target.value)}
                    required
                    fullWidth
                    multiline
                    minRows={4}
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Company Phone Number"
                    value={profileForm.company_phone}
                    onChange={(event) => updateProfileField('company_phone', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Website"
                    value={profileForm.website}
                    onChange={(event) => updateProfileField('website', event.target.value)}
                    required
                    fullWidth
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Company Type"
                    value={profileForm.company_type}
                    onChange={(event) => updateProfileField('company_type', event.target.value)}
                    required
                    fullWidth
                  >
                    {companyTypeOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="CMMC Compliance Level"
                    value={profileForm.cmmc_compliance_level}
                    onChange={(event) => updateProfileField('cmmc_compliance_level', event.target.value)}
                    fullWidth
                  >
                    {cmmcOptions.map((option) => (
                      <MenuItem key={option.value} value={option.value}>
                        {option.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <TextField
                    select
                    label="Government Entity"
                    value={profileForm.government_entity}
                    onChange={(event) => updateProfileField('government_entity', event.target.value)}
                    required
                    fullWidth
                  >
                    <MenuItem value="true">Yes</MenuItem>
                    <MenuItem value="false">No</MenuItem>
                  </TextField>
                </Grid>
              </Grid>
            </Grid>

            <Grid item xs={12} md={4}>
              <Stack
                spacing={2}
                sx={{
                  minHeight: 260,
                  display: 'grid',
                  alignContent: 'center',
                  justifyItems: 'center',
                }}
              >
                <Box sx={{ width: '100%', minHeight: 180, display: 'grid', placeItems: 'center' }}>
                  {profileLogoPreviewUrl ? (
                    <Box
                      component="img"
                      src={profileLogoPreviewUrl}
                      alt={`${partnerName} logo`}
                      sx={{ maxWidth: '100%', maxHeight: 150, objectFit: 'contain' }}
                    />
                  ) : (
                    <Avatar
                      variant="rounded"
                      sx={{ width: 92, height: 92, bgcolor: 'grey.100', color: 'text.secondary' }}
                    >
                      <BusinessRoundedIcon sx={{ fontSize: 44 }} />
                    </Avatar>
                  )}
                </Box>

                <Button
                  component="label"
                  variant="outlined"
                  startIcon={uploadingLogoKey === 'profile' ? <CircularProgress size={18} /> : <CloudUploadRoundedIcon />}
                  disabled={profileBusy || !partner?.partner_id}
                >
                  {uploadingLogoKey === 'profile' ? 'Uploading' : 'Upload Logo'}
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={(event) => {
                      const file = event.target.files?.[0];
                      event.target.value = '';
                      uploadLogo(file);
                    }}
                  />
                </Button>
                <Typography variant="caption" color="text.secondary">
                  Optional
                </Typography>
              </Stack>
            </Grid>
          </Grid>

          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
            <Button type="button" variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => openStep('welcome')}>
              Back
            </Button>
            <Button
              type="submit"
              variant="contained"
              startIcon={savingKey === 'profile' ? <CircularProgress color="inherit" size={18} /> : <SaveRoundedIcon />}
              disabled={profileBusy}
            >
              {savingKey === 'profile' ? 'Saving' : 'Continue'}
            </Button>
          </Stack>
        </Stack>
      </Box>
    );
  }

  function renderInviteStep() {
    return (
      <Stack spacing={3}>
        {error ? <Alert severity="error">{error}</Alert> : null}

        <Typography color="text.secondary">
          Invite users to this partner. After they accept, business unit access can be assigned from Partner Administration.
        </Typography>
        <Box component="form" onSubmit={submitInvite}>
          <Grid container spacing={2}>
            <Grid item xs={12} md={4}>
              <TextField
                label="Partner User Email"
                type="email"
                value={inviteForm.email}
                onChange={(event) => updateInviteField('email', event.target.value)}
                required
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={4}>
              <TextField
                label="Full Name"
                value={inviteForm.full_name}
                onChange={(event) => updateInviteField('full_name', event.target.value)}
                fullWidth
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                select
                label="Role"
                value={inviteForm.role}
                onChange={(event) => updateInviteField('role', event.target.value)}
                required
                fullWidth
              >
                {inviteRoleOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={1} sx={{ display: 'flex', alignItems: 'stretch' }}>
              <Button
                type="submit"
                variant="contained"
                aria-label="Send invitation"
                disabled={inviteBusy}
                sx={{ minWidth: { xs: '100%', md: 48 } }}
              >
                {savingKey === 'invite' ? (
                  <CircularProgress color="inherit" size={18} />
                ) : (
                  <PersonAddAltRoundedIcon />
                )}
              </Button>
            </Grid>
          </Grid>
        </Box>

        <Divider />

        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 1.5 }}>
            Invitations Sent This Session
          </Typography>
          {sentInvitations.length ? (
            <List disablePadding>
              {sentInvitations.map((invitation) => (
                <ListItem
                  key={invitation.invite_id || invitation.email}
                  divider
                  disableGutters
                  secondaryAction={(
                    <Chip
                      size="small"
                      label="Invited"
                      variant="outlined"
                    />
                  )}
                >
                  <ListItemText
                    primary={invitation.full_name || invitation.email}
                    secondary={`${invitation.email} - ${inviteRoleOptions.find((option) => option.value === invitation.role)?.label || 'User'}`}
                    primaryTypographyProps={{ fontWeight: 700 }}
                    secondaryTypographyProps={{ sx: { pr: 12 } }}
                  />
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No invitations sent yet.
            </Typography>
          )}
        </Box>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5} justifyContent="space-between">
          <Button type="button" variant="outlined" startIcon={<ArrowBackRoundedIcon />} onClick={() => openStep('partner_profile')}>
            Back
          </Button>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={1.5}>
            <Button
              type="button"
              variant="outlined"
              onClick={confirmFinishWithoutInvitations}
              disabled={inviteBusy || sentInvitations.length > 0}
            >
              Finish Without Invitations
            </Button>
            <Button
              type="button"
              variant="contained"
              startIcon={savingKey === 'complete' ? <CircularProgress color="inherit" size={18} /> : <HomeRoundedIcon />}
              disabled={inviteBusy || sentInvitations.length === 0}
              onClick={completeOnboarding}
            >
              {savingKey === 'complete' ? 'Finishing' : 'Finish Onboarding'}
            </Button>
          </Stack>
        </Stack>
      </Stack>
    );
  }

  return (
    <>
      <CssBaseline enableColorScheme />
      <Box
        sx={{
          minHeight: '100vh',
          bgcolor: '#f4f7fb',
          color: '#17283d',
          px: { xs: 2, md: 4 },
          py: { xs: 3, md: 5 },
        }}
      >
        <Stack spacing={3} sx={{ maxWidth: 1120, mx: 'auto' }}>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'flex-start', sm: 'center' }}>
            <Box
              component="img"
              src="/images/AMX-xConsole-Logo.png"
              alt="AMX xConsole"
              sx={{ width: 190, height: 72, objectFit: 'contain' }}
            />
            <Box sx={{ flex: 1 }}>
              <Typography variant="h4" sx={{ fontWeight: 800 }}>
                {pageTitle}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {partnerName}
              </Typography>
            </Box>
          </Stack>

          <Paper
            elevation={0}
            sx={{
              p: { xs: 2.5, md: 4 },
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 2,
            }}
          >
            <Stack spacing={3}>
              {renderStepTracker()}
              <Divider />
              {currentStep === 'welcome' ? renderWelcomeStep() : null}
              {currentStep === 'partner_profile' ? renderProfileStep() : null}
              {currentStep === 'partner_users' ? renderInviteStep() : null}
            </Stack>
          </Paper>
        </Stack>
      </Box>

      <Dialog open={skipConfirmOpen} onClose={() => setSkipConfirmOpen(false)}>
        <DialogTitle>Finish Without User Invitations?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            You can invite partner users later from Partner Administration.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSkipConfirmOpen(false)} disabled={savingKey === 'complete'}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={completeOnboarding}
            disabled={savingKey === 'complete'}
            startIcon={savingKey === 'complete' ? <CircularProgress color="inherit" size={18} /> : null}
          >
            {savingKey === 'complete' ? 'Finishing' : 'Finish'}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
